import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import { cva, type VariantProps } from 'class-variance-authority';
import type * as React from 'react';

import { cn } from '../../styles/lib/utils';

const radioCardVariants = cva('', {
  variants: {
    layout: {
      grid: 'grid',
      stack: 'flex flex-col',
    },
  },
  defaultVariants: {
    layout: 'grid',
  },
});

const radioCardItemVariants = cva([
  'm-1 block rounded-lg border p-2 text-left transition-colors cursor-pointer',
  'focus-ring',
  'data-[state=unchecked]:bg-white data-[state=unchecked]:border-grey-30',
  'data-[state=unchecked]:hover:bg-grey-10 data-[state=unchecked]:hover:shadow-md',
  'data-[state=checked]:bg-blue-10 data-[state=checked]:border-blue-50',
  'disabled:cursor-not-allowed disabled:opacity-60',
  'disabled:data-[state=unchecked]:hover:bg-white disabled:data-[state=unchecked]:hover:shadow-none',
]);

type RadioCardProps<TValue extends string> = Omit<
  React.ComponentProps<typeof RadioGroupPrimitive.Root>,
  'value' | 'defaultValue' | 'onValueChange'
> &
  VariantProps<typeof radioCardVariants> & {
    value?: TValue;
    defaultValue?: TValue;
    onValueChange?: (value: TValue) => void;
  };

/** Generic over the value type so callers keep their own union instead of casting Radix's `string` back. */
function RadioCard<TValue extends string>({ className, layout, onValueChange, ...props }: RadioCardProps<TValue>) {
  return (
    <RadioGroupPrimitive.Root
      data-slot="radio-card"
      className={cn(radioCardVariants({ layout }), className)}
      onValueChange={onValueChange as ((value: string) => void) | undefined}
      {...props}
    />
  );
}

type RadioCardItemProps = React.ComponentProps<typeof RadioGroupPrimitive.Item> & {
  label: React.ReactNode;
  detail?: React.ReactNode;
  description?: React.ReactNode;
};

function RadioCardItem({ className, label, detail, description, children, ...props }: RadioCardItemProps) {
  return (
    <RadioGroupPrimitive.Item data-slot="radio-card-item" className={cn(radioCardItemVariants(), className)} {...props}>
      <span data-slot="radio-card-label" className="block font-medium">
        {label}
      </span>
      {detail !== undefined && (
        <span data-slot="radio-card-detail" className="block">
          {detail}
        </span>
      )}
      {description !== undefined && (
        <span data-slot="radio-card-description" className="block text-2xs text-secondary">
          {description}
        </span>
      )}
      {children}
    </RadioGroupPrimitive.Item>
  );
}

export { RadioCard, RadioCardItem };
